import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePartnerInvite } from '../../../../hooks/partners/usePartnerInvite'

import Icon from '../../../ui/Icon'
import { Button } from '../../../buttons/Button'
import { TextInput } from '../../../input/TextInput'

import { useApp } from '../../../../contexts/AppContext'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { InputType } from '../../../../constants/inputTypes'

type PartnerInviteProps = {
  onInviteSuccess?: () => void
}

const PartnerInvite = ({ onInviteSuccess }: PartnerInviteProps) => {
  const { showBanner } = useApp()
  const { t } = useTranslation('partners')
  const [email, setEmail] = useState('')
  const [isEmailValid, setIsEmailValid] = useState(false)

  const { partnerInvite, loading, error, success, resetState } =
    usePartnerInvite()

  useEffect(() => {
    if (error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: error,
      })
    }
    if (success) {
      showBanner({
        kind: BannerKind.OK,
        msg: success,
      })
      setEmail('')
      setIsEmailValid(false)
      onInviteSuccess?.()
    }

    resetState()
  }, [error, success, resetState, showBanner, onInviteSuccess])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!isEmailValid) {
      return
    }

    await partnerInvite(email.trim())
  }

  return (
    <div className="ph-card partner-invite mb-24">
      <h3 className="mb-16">{t('partners.invite.title')}</h3>
      <form
        className="row g-2 align-items-md-end partner-invite-form"
        onSubmit={handleSubmit}
      >
        <div className="col-12 col-md">
          <TextInput
            name="email"
            value={email}
            type={InputType.Email}
            placeHolder={t('partners.invite.placeholder')}
            errorMessage={t('partners.invite.invalid-email')}
            onChange={(_, value, isValid) => {
              setEmail(value)
              setIsEmailValid(isValid && value.trim().length > 0)
            }}
          />
        </div>
        <div className="col-12 col-md-auto">
          <Button
            type="submit"
            label={t('partners.invite.send-btn')}
            icon={<Icon name="mail" size={14} />}
            className="w-100 w-md-auto text-nowrap"
            disabled={!isEmailValid}
            loading={loading}
          />
        </div>
      </form>
      <p className="muted tiny mt-8">{t('partners.invite.hint')}</p>
    </div>
  )
}

export default PartnerInvite
